import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import API from "@/lib/api";
import Header from "@/components/app/Header";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ArrowRight, MapPin, House, User, Kanban, NotePencil } from "@phosphor-icons/react";

const COLUMN_COLORS = {
  "Instruction": { border: "border-t-blue-500", bg: "bg-blue-50", text: "text-blue-700" },
  "Searches & Enquiries": { border: "border-t-violet-500", bg: "bg-violet-50", text: "text-violet-700" },
  "Mortgage Offer": { border: "border-t-amber-500", bg: "bg-amber-50", text: "text-amber-700" },
  "Contract Drafting & Approval": { border: "border-t-cyan-500", bg: "bg-cyan-50", text: "text-cyan-700" },
  "Exchange of Contracts": { border: "border-t-emerald-500", bg: "bg-emerald-50", text: "text-emerald-700" },
  "Completion": { border: "border-t-green-600", bg: "bg-green-50", text: "text-green-700" },
};

const DEFAULT_COL = { border: "border-t-slate-400", bg: "bg-slate-50", text: "text-slate-700" };

export default function KanbanPage() {
  const navigate = useNavigate();
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scope, setScope] = useState("mine");
  // {case_id: file_notes_count} — solicitor-only. Used to badge each
  // Kanban card with the same internal-notes counter shown on the
  // dashboard so you can spot matters with pending notes at a glance.
  const [fileNoteCounts, setFileNoteCounts] = useState({});

  const fetchKanban = useCallback(async () => {
    try {
      const { data } = await API.get(`/cases-kanban?scope=${scope}`);
      setColumns(data);
    } catch (err) {
      console.error("Failed to fetch kanban data", err);
    } finally {
      setLoading(false);
    }
    try {
      const { data } = await API.get("/file-notes/counts");
      setFileNoteCounts(data || {});
    } catch {
      // non-solicitor or transient — silent
    }
  }, [scope]);

  useEffect(() => { fetchKanban(); }, [fetchKanban]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="flex justify-center py-20">
          <div className="w-8 h-8 border-2 border-slate-300 border-t-blue-600 rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  const totalCases = columns.reduce((sum, col) => sum + col.cases.length, 0);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="kanban-page">
      <Header />
      <main className="max-w-[1600px] mx-auto px-4 sm:px-6 py-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Pipeline
            </h1>
            <p className="text-sm text-slate-500 mt-0.5" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {totalCases} active case{totalCases !== 1 ? "s" : ""} across {columns.length} stages
            </p>
          </div>
          <div className="flex items-center bg-white border border-slate-200 rounded-lg p-0.5" data-testid="kanban-scope-toggle">
            <button onClick={() => { setScope("mine"); setLoading(true); }} data-testid="kanban-scope-mine"
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${scope === "mine" ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-900"}`}>
              My Cases
            </button>
            <button onClick={() => { setScope("all"); setLoading(true); }} data-testid="kanban-scope-all"
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${scope === "all" ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-900"}`}>
              All Cases
            </button>
          </div>
        </div>

        {columns.length === 0 ? (
          <div className="text-center py-16 bg-white border border-slate-200 rounded-lg" data-testid="kanban-empty">
            <Kanban weight="thin" className="w-14 h-14 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500 font-medium">No active cases in the pipeline</p>
          </div>
        ) : (
          <div className="flex gap-4 overflow-x-auto pb-4" data-testid="kanban-board">
            {columns.map((col) => {
              const colors = COLUMN_COLORS[col.label] || DEFAULT_COL;
              return (
                <div
                  key={col.label}
                  className={`shrink-0 w-72 bg-white border border-slate-200 rounded-lg border-t-[3px] ${colors.border} shadow-sm flex flex-col`}
                  data-testid={`kanban-column-${col.label.replace(/\s+/g, "-").toLowerCase()}`}
                >
                  {/* Column header */}
                  <div className="px-4 py-3 border-b border-slate-100">
                    <div className="flex items-center justify-between">
                      <h3 className="text-xs font-semibold text-slate-900 uppercase tracking-wide" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        {col.label}
                      </h3>
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${colors.bg} ${colors.text}`}>
                        {col.cases.length}
                      </span>
                    </div>
                  </div>

                  {/* Cards */}
                  <ScrollArea className="flex-1 max-h-[calc(100vh-240px)]">
                    <div className="p-3 space-y-2.5">
                      {col.cases.length === 0 ? (
                        <p className="text-xs text-slate-400 text-center py-4">No cases</p>
                      ) : (
                        col.cases.map((c) => (
                          <button
                            key={c.id}
                            onClick={() => navigate(`/cases/${c.id}`)}
                            data-testid={`kanban-card-${c.id}`}
                            className="w-full text-left p-3 bg-white border border-slate-200 rounded-lg hover:shadow-md hover:-translate-y-[1px] transition-all duration-150 group"
                          >
                            <div className="flex items-start justify-between mb-1.5">
                              <h4 className="text-sm font-semibold text-slate-900 leading-snug group-hover:text-blue-600 transition-colors line-clamp-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                {c.title}
                              </h4>
                              <span className={`ml-2 shrink-0 text-[10px] font-medium px-1.5 py-0.5 rounded-full ${c.case_type === "buying" ? "bg-blue-50 text-blue-600" : "bg-amber-50 text-amber-600"}`}>
                                {c.case_type}
                              </span>
                              {fileNoteCounts[c.id] > 0 && (
                                <span
                                  className="ml-1 shrink-0 inline-flex items-center gap-0.5 text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-100"
                                  title={`${fileNoteCounts[c.id]} internal file note${fileNoteCounts[c.id] === 1 ? "" : "s"}`}
                                  data-testid={`kanban-notes-badge-${c.id}`}
                                >
                                  <NotePencil weight="bold" className="w-2 h-2" />
                                  {fileNoteCounts[c.id]}
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-slate-500 flex items-center gap-1 mb-2 truncate">
                              <MapPin weight="fill" className="w-3 h-3 shrink-0" />{c.property_address}
                            </p>
                            {c.case_ref && (
                              <p className="text-[10px] text-slate-400 font-mono mb-1.5 truncate" data-testid={`kanban-ref-${c.id}`}>
                                #{c.case_ref}
                              </p>
                            )}
                            {scope === "all" && c.solicitor_name && (
                              <p className="text-[10px] text-blue-600 font-medium mb-1.5 truncate">Solicitor: {c.solicitor_name}</p>
                            )}
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-1.5">
                                {c.client_name ? (
                                  <span className="text-[10px] text-slate-500 flex items-center gap-0.5">
                                    <User weight="fill" className="w-2.5 h-2.5" />{c.client_name}
                                  </span>
                                ) : (
                                  <span className="text-[10px] text-slate-400 italic">No client</span>
                                )}
                              </div>
                              <div className="flex items-center gap-1.5">
                                <div className="w-12 h-1 bg-slate-100 rounded-full overflow-hidden">
                                  <div className="h-full bg-blue-500 rounded-full" style={{ width: `${c.progress}%` }} />
                                </div>
                                <span className="text-[10px] text-slate-400 font-medium">{c.progress}%</span>
                              </div>
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  </ScrollArea>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
